import React, { useCallback, useEffect, useMemo, useState, useRef  } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faPlus,
  faTrashCan,
  faUserGraduate,
  faUsersGear,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APISD } from "../../../../../config/apisd";
import { APIMS } from "../../../../../config/apims";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";
import "bulma/css/bulma.css";
import "../../../../../index.css";

const API_LIMIT = 1000;
const text = (value) => (value == null ? "" : String(value));

const getInitials = (name) => {
  const words = text(name).trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return "-";

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

export default function WaliKelas() {
  document.title = "Wali Kelas";


  const token = localStorage.getItem("token");
  const storageBranch = localStorage.getItem("ysb_branch_id");
  const storageLevel = localStorage.getItem("level");

  const [page] = useState(1);
  const [limit] = useState(1000);
  const [ascending] = useState(1);
  const [descending] = useState(0);
  const [keyword] = useState("");

  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);

  const firstLoadDone = useRef(false);
  const initSchoolDone = useRef(false);

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const [deletingClassId, setDeletingClassId] = useState("");

  const [rows, setRows] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [search, setSearch] = useState("");

  const [getDataSemesterMap, setGetDataSemesterMap] = useState([]);
  const [getDataBranch, setGetDataBranch] = useState([]);
  const [getDataSchool, setGetDataSchool] = useState([]);
  
  const [namaTA, setNamaTA] = useState("");
  const [namaSmstr, setNamaSmstr] = useState("");
 
  const [formFilter, setFormFilter] = useState({
    ysb_semester_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  const location = useLocation();
  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const storageLevel = localStorage.getItem("level");
    const storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
    } else {
      const foundPermission = storageItems.find(
        (item) => item.menu?.url === location.pathname
      );

      if (!foundPermission) {
        navigate("/dashboard");
      } else {
        setPermission({
          create: foundPermission.create,
          read: foundPermission.read,
          update: foundPermission.update,
          delete: foundPermission.delete,
        });
      }
    }
  }, [location.pathname, navigate]);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const yearOptions = useMemo(
    () => [
      ...new Set(getDataSemesterMap.map((item) => item?.name_year).filter(Boolean)),
    ],
    [getDataSemesterMap],
  );
  const semesterOptions = useMemo(
    () =>
      getDataSemesterMap
        .filter((item) => item?.name_year === namaTA)
        .sort((a, b) => Number(a?.semester) - Number(b?.semester)),
    [namaTA, getDataSemesterMap],
  );

  const isMounted = useRef(true);
  const abortControllers = useRef([]);

  useEffect(() => {
     isMounted.current = true;
     return () => {
       isMounted.current = false;
       abortControllers.current.forEach((c) => c.abort());
     };
   }, []);
 
  const schoolRequestRef = useRef(0);

  // SORT sekolah: SD → SMP → SMA
  const sortSchools = (schools) => {
    const order = ["SD", "SMP", "SMA"];
    return [...schools].sort((a, b) => {
      const jenjangA = order.findIndex((j) => (a.school_name || "").toUpperCase().includes(j));
      const jenjangB = order.findIndex((j) => (b.school_name || "").toUpperCase().includes(j));
      return jenjangA - jenjangB;
    });
  };

  const removeBasementBekasi = (schools, branchId) => {
    if (branchId === "BKS") return schools.filter((s) => s.school_code !== "BASEMENT_BEKASI");
    return schools;
  };

  // =========================
  // INIT: Branch + Semester 
  // =========================
  const fetchInit = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    abortControllers.current.push(controller);

    try {
      const [branchRes, semesterRes] = await Promise.all([
        APIMS.get(
          `/api/access/branches?page=${page}&limit=${limit}&ascending=${ascending}&search=${keyword}&branch=${storageBranch}&level=${storageLevel}`,
          { ...fetchParams, signal: controller.signal }
        ),
        APIMS.get(
          `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
          { ...fetchParams, signal: controller.signal }
        ),
      ]);

      if (!isMounted.current) return;
      if (!(branchRes?.data?.data || []).length) setLoading(false);

      // Branch default pertama
      if (branchRes?.status === 200) {
        const branches = branchRes.data.data || [];
        setGetDataBranch(branches);
        if (branches.length > 0) {
          const firstBranch = branches[0].branch_code;
          setFormFilter((prev) => ({ ...prev, ysb_branch_id: firstBranch }));
        }
      }

      // Semester aktif
      if (semesterRes?.status === 200) {
        const semesters = semesterRes.data.data || [];
        const active = semesters.find((s) => Number(s.status) === 1) || semesters[0];
        setGetDataSemesterMap(semesters);
        if (active) {
          setNamaTA(active.name_year || "");
          setNamaSmstr(active.semester || "");
          setFormFilter((prev) => ({ ...prev, ysb_semester_id: active.id }));
        }
      }
    } catch (err) {
      if (controller.signal.aborted || !isMounted.current) return;
      setLoading(false);
      swal({
        title: "Failed",
        text: err?.response?.data?.message || err.message || "Error",
        icon: "error",
      });
    } 

  }, [ascending, descending, fetchParams, keyword, limit, page, storageBranch, storageLevel]);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  // =========================
  // Fetch sekolah saat branch berubah
  // =========================
  const fetchSchoolsByBranch = useCallback(
    async (branchId) => {
      const requestId = ++schoolRequestRef.current;
      initSchoolDone.current = false;
      setLoadingSchool(true); // ⬅️ school mulai load
      try {
        const res = await APIMS.get(
          `/api/access/schools?page=${page}&limit=${limit}&ascending=${ascending}&branch=${branchId}&level=${storageLevel}`,
          fetchParams
        );
        if (!isMounted.current || requestId !== schoolRequestRef.current) return;
        if (res?.status === 200) {
          let schools = res.data.data || [];

          // ✅ FILTER ULANG UNTUK ROLE DEVELOPER
          if (storageLevel === "developer" || storageLevel === "superadmin") {
            schools = schools.filter(
              (s) => s.ysb_branch_id === branchId
            );
          }

          schools = removeBasementBekasi(schools, branchId);
          schools = sortSchools(schools);

          setGetDataSchool(schools);

          // default sekolah pertama
          if (schools.length > 0) {
            setFormFilter((prev) => ({
              ...prev,
              ysb_school_id: schools[0].school_code,
            }));
          } else {
            setFormFilter((prev) => ({ ...prev, ysb_school_id: ""}));
          }
        }
      } catch (err) {
        if (isMounted.current && requestId === schoolRequestRef.current) {
          setGetDataSchool([]);
          setFormFilter((prev) => ({ ...prev, ysb_school_id: "" }));
        }
      } finally {
          if (isMounted.current && requestId === schoolRequestRef.current) {
          initSchoolDone.current = true;
          setLoadingSchool(false); // ⬅️ school SELESAI
          setLoading(false);       // ⬅️ shimmer BARU MATI DI SINI
          }
      }
    },[ascending, fetchParams, limit, page, storageLevel]
  );

  useEffect(() => {
    if (formFilter.ysb_branch_id) fetchSchoolsByBranch(formFilter.ysb_branch_id);
  }, [formFilter.ysb_branch_id, fetchSchoolsByBranch]);

  // One filter state for the year dropdown, branch, school, and semester tabs.
  const semesterCache = useRef({});
  const tableRequestRef = useRef(0);

  const isFilterComplete =
    !!namaTA && !!formFilter.ysb_semester_id &&
    !!formFilter.ysb_branch_id && !!formFilter.ysb_school_id && !loadingSchool;

  const GetResponseData = useCallback(async () => {
    if (!namaTA || !formFilter.ysb_semester_id ||
        !formFilter.ysb_branch_id || !formFilter.ysb_school_id || loadingSchool) return;

    const requestId = ++tableRequestRef.current;

    setIsFetching(true);
    setSearch("");

    try {
      const entries = await Promise.all(semesterOptions.map(async (semester) => {
        const response = await APISD.post("/api/walas-assignments", {
          page: 1,
          limit: API_LIMIT,
          ascending: true,
          search: "",
          ysb_academic_year_id: semester.ysb_academic_year_id,
          ysb_semester_id: semester.id,
          ysb_branch_id: formFilter.ysb_branch_id,
          ysb_school_id: formFilter.ysb_school_id,
        }, fetchParams);
        return [String(semester.id), Array.isArray(response?.data?.data) ? response.data.data : []];
      }));

      if (!isMounted.current || requestId !== tableRequestRef.current) return;

      semesterCache.current = Object.fromEntries(entries);

      setRows(semesterCache.current[String(formFilter.ysb_semester_id)] || []);

      setHasSearched(true);
      setIsFilterDirty(false);

    } catch (error) {
      if (!isMounted.current || requestId !== tableRequestRef.current) return;
      setRows([]);
      setHasSearched(false);
      setIsFilterDirty(true);
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal mengambil data wali kelas",
        icon: "error",
      });
    } finally {
      if (isMounted.current && requestId === tableRequestRef.current) setIsFetching(false);
    }
  }, [namaTA, formFilter, semesterOptions, fetchParams, loadingSchool]);

  useEffect(() => {
    if (firstLoadDone.current || isFilterDirty || loading ||
        !initSchoolDone.current || !isFilterComplete) return;
    firstLoadDone.current = true;
    GetResponseData();
  }, [loading, isFilterComplete, isFilterDirty, GetResponseData]);

  const invalidateFilter = () => {
    ++tableRequestRef.current;
    semesterCache.current = {};
    setRows([]);
    setSearch("");
    setIsFetching(false);
    setIsFilterDirty(true);
    setHasSearched(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "ysb_branch_id") {
      ++schoolRequestRef.current;
      initSchoolDone.current = false;
      setGetDataSchool([]);
      setLoadingSchool(!!value);
    }
    setFormFilter((prev) => ({
      ...prev, [name]: value,
      ...(name === "ysb_branch_id" ? { ysb_school_id: "" } : {}),
    }));
    invalidateFilter();
  };

  const changeYear = (event) => {
    const year = event.target.value;
    const options = getDataSemesterMap
      .filter((item) => item?.name_year === year)
      .sort((a, b) => Number(a?.semester) - Number(b?.semester));
    const selected = options.find((item) => Number(item?.status) === 1) || options[0];
    setNamaTA(year);
    setNamaSmstr(selected?.semester || "");
    setFormFilter((prev) => ({ ...prev, ysb_semester_id: selected?.id || "" }));
    invalidateFilter();
  };

  const handleCari = async () => {
    if (loadingSchool) {
      swal({ title: "Mohon tunggu", text: "Data sekolah sedang dimuat.", icon: "info" });
      return;
    }
    if (!isFilterComplete) {
      swal({ title: "Filter belum lengkap", text: "Pastikan Tahun Ajaran, Semester, Cabang, dan Sekolah sudah dipilih.", icon: "warning" });
      return;
    }
    await GetResponseData();
  };

  const switchSemester = (semester) => {
    if (!semester?.id || String(semester.id) === String(formFilter.ysb_semester_id)) return;
    setNamaSmstr(semester.semester || "");
    setFormFilter((prev) => ({ ...prev, ysb_semester_id: semester.id }));
    setSearch("");
    setRows(semesterCache.current[String(semester.id)] || []);
  };

  const openWalasModal = (item) => {
    const hasTeachers = (item?.teachers || []).length > 0;

    setSelectedData(item);
    if (hasTeachers) setModalUpdate(true);
    else setModalAdd(true);
  };

  const deleteAllWalasInClass = async (item) => {
    const teachers = item?.teachers || [];
    if (teachers.length === 0) return;

    const assignmentIds = teachers
      .map((teacher) => teacher?.assignment_id)
      .filter(Boolean);

    if (assignmentIds.length !== teachers.length) {
      swal({
        title: "Gagal",
        text: "ID assignment wali kelas tidak ditemukan",
        icon: "error",
      });
      return;
    }

    const confirmed = await swal({
      title: "Hapus semua walas?",
      text: `Semua wali kelas di kelas ${toTitleCase(item?.name_kelas) || "ini"} pada semester ${namaSmstr} akan dihapus.`,
      icon: "warning",
      buttons: ["Batal", "Ya, hapus"],
      dangerMode: true,
    });

    if (!confirmed) return;

    setDeletingClassId(String(item?.ysb_kelas_id || ""));
    try {
      await Promise.all(
        assignmentIds.map((assignmentId) =>
          APISD.delete(
            `/api/walas-assignments/${encodeURIComponent(assignmentId)}`,
            fetchParams,
          ),
        ),
      );

      swal({
        title: "Berhasil",
        text: "Semua wali kelas pada kelas tersebut berhasil dihapus",
        icon: "success",
        timer: 1800,
        buttons: false,
      });
      await GetResponseData();
    } catch (error) {
      swal({
        title: "Gagal",
        text:
          error?.response?.data?.message ||
          "Gagal menghapus semua wali kelas pada kelas tersebut",
        icon: "error",
      });
    } finally {
      setDeletingClassId("");
    }
  };

  const displayedRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rows
      .filter(
        (item) =>
          !keyword ||
          [
            item?.name_level,
            item?.name_kelas,
            ...(item?.teachers || []).map((teacher) => teacher?.full_name),
          ].some((value) => text(value).toLowerCase().includes(keyword)),
      )
      .sort(
        (a, b) =>
          text(a?.name_level).localeCompare(text(b?.name_level), "id", {
            numeric: true,
          }) ||
          text(a?.name_kelas).localeCompare(text(b?.name_kelas), "id", {
            numeric: true,
          }),
      );
  }, [rows, search]);

  const displayedRowsWithLevelSpan = useMemo(
    () =>
      displayedRows.map((item, index, currentRows) => {
        const currentLevel = text(item?.name_level).trim();
        const previousLevel =
          index > 0 ? text(currentRows[index - 1]?.name_level).trim() : null;

        if (index > 0 && currentLevel === previousLevel) {
          return { ...item, levelRowSpan: 0 };
        }

        let levelRowSpan = 1;

        for (let nextIndex = index + 1; nextIndex < currentRows.length; nextIndex += 1) {
          const nextLevel = text(currentRows[nextIndex]?.name_level).trim();

          if (nextLevel !== currentLevel) break;
          levelRowSpan += 1;
        }

        return { ...item, levelRowSpan };
      }),
    [displayedRows],
  );
  const complete =
    !!formFilter.ysb_semester_id &&
    !!formFilter.ysb_branch_id &&
    !!formFilter.ysb_school_id &&
    !loadingSchool;
  const context = {
    academicYear: namaTA,
    semesterList: semesterOptions,
    branchId: formFilter.ysb_branch_id,
    branchName: getDataBranch.find(
      (item) => item?.branch_code === formFilter.ysb_branch_id,
    )?.branch_name,
    schoolId: formFilter.ysb_school_id,
    schoolName: getDataSchool.find(
      (item) => item?.school_code === formFilter.ysb_school_id,
    )?.school_name,
    classes: rows,
  };

  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

  const toTitleCase = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";
    return s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
  };

  const toTitleCaseGelar = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";

    return s
      .split(" ")
      .map((word) => {
        // khusus gelar yang ada titik, contoh: s.si. -> S.Si.
        if (word.includes(".")) {
          return word
            .split(".")
            .map((part) => {
              if (!part) return "";
              return part.charAt(0).toUpperCase() + part.slice(1);
            })
            .join(".");
        }

        // normal title case, contoh: helmy -> Helmy
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  return (
    <div className="body sims-page">
      {modalAdd && (
        <ModalAdd
          show
          context={context}
          dataCreate={selectedData}
          GetResponseData={() => GetResponseData()}
          onHide={() => {
            setModalAdd(false);
            setSelectedData(null);
          }}
        />
      )}
      {modalUpdate && selectedData && (
        <ModalUpdate
          show
          context={context}
          dataUpdate={selectedData}
          GetResponseData={() => GetResponseData()}
          onHide={() => {
            setModalUpdate(false);
            setSelectedData(null);
          }}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faUserGraduate} />
            </span>
            <h4 className="page-title-text">Wali Kelas</h4>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {loading ? (
          <div className="sims-filter-card sims-filter-card-two-shimmer">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <div className="sims-filter-card sims-filter-card-two">
            <div className="sims-filter-field">
              <label className="sims-filter-label">Tahun Ajaran</label>
              <select
                className="sims-filter-select"
                value={namaTA}
                onChange={changeYear}
              >
                <option value="" disabled>
                  Pilih Tahun Ajaran
                </option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}{getDataSemesterMap.some((item) => item.name_year === year && Number(item.status) === 1) ? " (Aktif)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Cabang</label>
              <select
                className="sims-filter-select"
                name="ysb_branch_id"
                value={formFilter.ysb_branch_id}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Pilih Cabang
                </option>
                {getDataBranch.map((item) => (
                  <option key={item?.branch_code} value={item?.branch_code}>
                    {item?.branch_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Sekolah</label>
              <select
                className="sims-filter-select"
                name="ysb_school_id"
                value={formFilter.ysb_school_id}
                onChange={handleChange}
                disabled={loadingSchool || !formFilter.ysb_branch_id}
              >
                <option value="" disabled>
                  {loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}
                </option>
                {getDataSchool.map((item) => (
                  <option key={item?.school_code} value={item?.school_code}>
                    {item?.school_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="sims-filter-action">
                <button
                  type="button"
                  className="sims-filter-button"
                  disabled={!complete || isFetching || (hasSearched && !isFilterDirty)}
                  onClick={handleCari}
                >
                  Tampilkan Data
                </button>
              </div>

          </div>
        )}

        {isFetching && (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        )}

        {!loading && !isFetching && !hasSearched && (
          <div className="sims-empty-state">
            Silakan klik <strong>Tampilkan Data</strong> untuk menampilkan data sesuai filter.
          </div>
        )}

        {hasSearched && !isFetching && (
          <>
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />
              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari kelas atau wali kelas"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="sims-table-tab-card ">
              <div className="sims-table-tab-head">
                <div className="sims-table-tab-title">
                  <h5>Data Wali Kelas - Semester {namaSmstr}</h5>
                </div>
                <div className="sims-table-tab-semester-switch">
                  {semesterOptions.map((semester) => (
                    <button
                      type="button"
                      key={semester?.id}
                      className={`sims-table-tab-semester ${String(formFilter.ysb_semester_id) === String(semester?.id) ? "is-active" : ""}`}
                      onClick={() => switchSemester(semester)}
                      disabled={isFetching}
                    >
                      Semester {semester?.semester}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="sims-table-tab-wrap">
                <table className="sims-table-tab walas-table-column-borders">
                  <thead>
                    <tr>
                      {/* <th className="sims-col-number">No</th> */}
                      <th>Tingkatan</th>
                      <th>Kelas</th>
                      <th>Wali Kelas</th>
                      <th className="sims-col-action">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Data wali kelas tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    ) : (
                      displayedRowsWithLevelSpan.map((item, index) => (
                        <tr
                          key={`${item?.ysb_kelas_id}-${item?.ysb_semester_id}`}
                        >
                          {/* <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {index + 1}
                            </span>
                          </td> */}
                          {item.levelRowSpan > 0 && (
                            <td
                              rowSpan={item.levelRowSpan}
                              className="text-center walas-level-cell"
                            >
                              <span className="sims-text-bold sims-number-badge" style={{fontSize: "14px"}}>
                                {item?.name_level || "-"}
                              </span>
                            </td>
                          )}
                          <td style={{fontSize: "13px"}}>{toTitleCase(item?.name_kelas) || "-"}</td>
                          <td>
                            {(item?.teachers || []).length > 0 ? (
                              <div className="walas-name-list">
                                {item.teachers.map((teacher, teacherIndex) => (
                                  <span
                                    className="walas-name-chip"
                                    key={
                                      teacher?.assignment_id ||
                                      teacher?.ysb_teacher_id ||
                                      `${teacher?.full_name}-${teacherIndex}`
                                    }
                                    title={teacher?.full_name || "Wali kelas"}
                                  >
                                    <span className="walas-name-initial">
                                      {getInitials(teacher?.full_name)}
                                    </span>

                                    <span className="walas-name-text">
                                      {toTitleCaseGelar(teacher?.full_name) || "-"}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="walas-name-empty">-</span>
                            )}
                          </td>
                          <td className="sims-col-action">
                            <div className="walas-action-group">
                            {!!(
                              (item?.teachers || []).length > 0
                                ? permission.update
                                : permission.create
                            ) && (
                              <button
                                type="button"
                                className={`sims-action-button config-matpel-table-button config-matpel-table-button-secondary ${
                                  (item?.teachers || []).length === 0
                                    ? "walas-add-button"
                                    : ""
                                }`}
                                title={(item?.teachers || []).length > 0 ? "Kelola wali kelas" : "Tambah wali kelas"}
                                onClick={() => openWalasModal(item)}
                              >
                                <FontAwesomeIcon
                                  icon={(item?.teachers || []).length > 0 ? faUsersGear : faPlus}
                                />
                                <span>
                                  {(item?.teachers || []).length > 0
                                    ? "Kelola Walas"
                                    : "Tambah Walas"}
                                </span>
                              </button>
                            )}
                            {(item?.teachers || []).length > 0 &&
                              !!permission.delete && (
                                <button
                                  type="button"
                                  className="walas-delete-button"
                                  title="Hapus semua walas dalam kelas"
                                  disabled={
                                    deletingClassId ===
                                    String(item?.ysb_kelas_id || "")
                                  }
                                  onClick={() => deleteAllWalasInClass(item)}
                                >
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`
        .walas-table-column-borders > thead > tr > th,
        .walas-table-column-borders > tbody > tr > td {
          border-right: 1px solid #d7e2f7 !important;
        }

        .walas-table-column-borders > thead > tr > th:last-child,
        .walas-table-column-borders > tbody > tr > td:last-child {
          border-right: 0 !important;
        }

        .walas-level-cell {
          vertical-align: middle !important;
          background-color: #f8fafffd !important;
          color: #0066ff !important;
          border : 1px solid #deeafc;
        }

        .walas-name-list {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          min-width: 190px;
        }

        .walas-name-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          max-width: 230px;
          min-height: 30px;
          padding: 6px 12px 6px 7px;
          border: 1px solid #d7e2f7;
          border-radius: 12px;
          background: #f8faff;
          color: #172554;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
          white-space: nowrap;
        }

        .walas-name-initial {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #dce8ff;
          color: #3f5fca;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: -0.2px;
        }

        .walas-name-text {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .walas-name-empty {
          color: #94a3b8;
        }

        .walas-action-group {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
        }

        .walas-action-group .config-matpel-table-button-secondary {
          width: auto;
          min-width: 132px;
          padding: 0 14px;
          gap: 7px;
        }

        .walas-action-group .walas-add-button {
          border-color: #3f5bea !important;
          background: #3f5bea !important;
          color: #ffffff !important;
          box-shadow: 0 4px 10px rgba(63, 91, 234, 0.2);
        }

        .walas-action-group .walas-add-button:hover {
          border-color: #324bc8 !important;
          background: #324bc8 !important;
          color: #ffffff !important;
        }

        .walas-delete-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 36px;
          width: 36px;
          height: 36px;
          border: 1px solid #fecaca;
          border-radius: 9px;
          background: #fff1f2;
          color: #dc2626;
          transition: 0.2s ease;
        }

        .walas-delete-button:hover:not(:disabled) {
          border-color: #fca5a5;
          background: #fee2e2;
        }

        .walas-delete-button:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        @media (max-width: 768px) {
          .walas-name-list {
            min-width: 160px;
          }

          .walas-name-chip {
            max-width: 190px;
          }
        }
      `}</style>
    </div>
  );
}
